import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { Bell, ChevronDown, ChevronUp, FileText, List } from "lucide-react";
import { observer } from "mobx-react-lite";
import { HTMLAttributes, ReactNode } from "react";

import {
  AlertType,
  ComplianceResponse,
  DefaultPatientAlertResponse,
  DefaultPatientEventResponse,
  EventCategory,
  GDMTResult,
  MeasurementRequiredData_Output,
  PatientMonitoringCallStatus,
  PatientMonitoringReportStatus,
  PatientMonitoringWatchKind,
  PatientMonitoringWatchStatus,
  PatientScheduleResponse,
  PatientState,
  PatientStatus,
  PermissionBit,
} from "../../../api";
import { ApiConnector } from "../../../api/api-connector";
import { useCountries } from "../../../catalogs/countries";
import { AgeSexBadge, ageSexOf } from "../../../components/age-sex-badge";
import { EventMarker } from "../../../components/monitoring-rooms/cell-events";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui-kit/base/dialog";
import { Tooltip } from "../../../components/ui-kit/base/tooltip";
import { Button } from "../../../components/ui-kit/button";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { RichTextContent } from "../../../components/ui-kit/rich-text-content";
import { ShortcutHint } from "../../../components/ui-kit/shortcut-hint";
import {
  alertKinds,
  callStatuses,
  patientStates,
  patientStatuses,
  reportStatuses,
  watchKinds,
  watchStatuses,
} from "../../../constants/enum-to-text";
import {
  hasMonitoringAccess,
  hasPermission,
} from "../../../constants/permissions";
import { DeviceConfigIcons } from "../../../utils/device-config-filter";
import { cn } from "../../../utils/shadcn-ui";
import { useShortcut } from "../../../utils/use-shortcut";
import { dateTimeOrNull, floatRounding } from "../../../utils/utils";
import {
  eventCategory,
  eventCategoryDetails,
  eventCategoryType,
} from "../events/enums";
import { useGdmt } from "../gdmt/gdmt";
import { GdmtIndicator } from "../gdmt/gdmt-indicator";
import { ComorbiditySummary, Patient } from "../patient";
import { scheduleToString } from "../schedule-utils";
import {
  ComplianceInfoTooltip,
  ComplianceScore,
  compliancePercents,
} from "./compliance-summary";

export const PatientProfileHeader = observer(
  function PatientProfileHeader(props: {
    patient: Patient;
    api: ApiConnector;
    // When false, the header renders no collapse/expand button — the owner
    // provides one elsewhere (the profile page puts it in the chart tabs'
    // measurements row). The ⌘I shortcut still toggles either way.
    showCollapseToggle?: boolean;
  }) {
    const { patient, api } = props;
    const showCollapseToggle = props.showCollapseToggle ?? true;
    const data = patient?.data;
    const countries = useCountries();
    const gdmt = useGdmt(patient.patientId);

    useShortcut(
      "i",
      () => {
        patient.headerCollapsed = !patient.headerCollapsed;
      },
      // Never worth swallowing a keystroke aimed at the text being edited.
      { skipWhileTyping: true },
    );

    if (!data) {
      return null;
    }

    const showMonitoring = hasMonitoringAccess(api.permissions);
    const showWaitingRoom = hasPermission(
      api.permissions,
      PermissionBit.VIEW_WAITING_ROOM,
    );
    const { age, sexLetter } = ageSexOf(data);

    if (patient.headerCollapsed) {
      const unseenAlertCount = (patient.alerts ?? []).filter(
        (a) => !a.alert_seen,
      ).length;
      const eventCount = (patient.events ?? []).length;

      return (
        <div className="mb-3 flex items-center justify-between gap-4 rounded-md border px-4 py-2">
          {/* Left: identity */}
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className="font-semibold">
              <span className="text-muted-foreground">#{data.patient_id}</span>{" "}
              {data.full_name}
            </span>
            <AgeSexBadge
              age={age}
              sexLetter={sexLetter}
              dateOfBirth={data.date_of_birth}
            />
            {showMonitoring && data.patient_state != null && (
              <PatientStateBadge state={data.patient_state} compact />
            )}
          </div>

          {/* Right: summary pills + expand */}
          <div className="flex items-center gap-2 shrink-0">
            <Tooltip
              content={
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1 font-semibold">
                    <FileText className="size-3" />
                    <Trans>Description</Trans>
                  </div>
                  <div className="whitespace-pre-wrap font-normal">
                    {data.description || "—"}
                  </div>
                </div>
              }
            >
              <SummaryPill icon={<FileText className="size-3 shrink-0" />}>
                <Trans>Description</Trans>
              </SummaryPill>
            </Tooltip>

            {showMonitoring &&
              (patient.events != null || patient.alerts != null) && (
                <Tooltip
                  content={
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 font-semibold">
                        <Bell className="size-3" />
                        <Trans>Monitoring Info</Trans>
                      </div>
                      {patient.events != null && (
                        <div className="font-normal">
                          <Plural
                            value={eventCount}
                            one="# event"
                            other="# events"
                          />
                        </div>
                      )}
                      {patient.alerts != null && (
                        <div className="font-normal">
                          <Plural
                            value={unseenAlertCount}
                            one="# unseen alert"
                            other="# unseen alerts"
                          />
                        </div>
                      )}
                    </div>
                  }
                >
                  <SummaryPill icon={<Bell className="size-3 shrink-0" />}>
                    <Trans>Monitoring</Trans>
                  </SummaryPill>
                </Tooltip>
              )}

            {showMonitoring && (
              <ComplianceScore
                compliance={patient.compliance}
                compact
                tooltip={
                  <ComplianceTooltip
                    config={data.self_measurement_config}
                    schedules={patient.schedules?.data}
                    compliance={patient.compliance}
                  />
                }
              />
            )}

            {showCollapseToggle && (
              <Button
                variant="subtle"
                size="sm"
                onClick={() => {
                  patient.headerCollapsed = false;
                }}
              >
                <ChevronDown className="size-4" />
                <Trans>Expand</Trans>
                <ShortcutHint>⌘I</ShortcutHint>
              </Button>
            )}
          </div>
        </div>
      );
    }

    const regionName =
      data.region !== undefined
        ? countries.getRegionSchema(data.country, data.region)?.region_name
        : undefined;

    // One flat grid so the cards can pair up in the middle band: stacked, then
    // two per row, then the info card at a third (3/9) and the rest 2/9 each.
    const gridClass = showMonitoring
      ? "grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-9"
      : "grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3";
    const infoSpan = showMonitoring ? "xl:col-span-3" : "";
    const cardSpan = showMonitoring ? "xl:col-span-2" : "";

    return (
      // No compensating negative top margin: on every route that renders this
      // header the page-title row is suppressed (it would only repeat the
      // identity shown here), so there is nothing above to pull up against.
      // mb-3 keeps a small gap to the content below.
      <div className="mb-3">
        <div className={gridClass}>
          <PatientInfoCard
            className={infoSpan}
            patientId={data.patient_id}
            fullName={data.full_name}
            age={age}
            sexLetter={sexLetter}
            dateOfBirth={data.date_of_birth}
            regionName={regionName}
            country={data.country}
            clinicalRiskScore={data.clinical_risk_score}
            gdmt={gdmt}
            patientState={data.patient_state}
            comorbidities={patient.comorbiditySummary}
            showStateBadge={showMonitoring}
            showWaitingRoom={showWaitingRoom}
            patientStatus={data.patient_status}
            callStatus={data.monitoring_call_status}
            reportStatus={data.monitoring_report_status}
            monitoringRoom={data.monitoring_watch_kind}
            watchStatus={data.monitoring_watch_status}
            race={data.race}
            height={data.height}
          />

          <NotesCard
            className={cardSpan}
            description={data.description}
            medicalNote={data.medical_monitoring_note}
            technicalNote={data.technical_monitoring_note}
            showMonitoringNotes={showMonitoring}
          />
          {showMonitoring && (
            <MonitoringInfoCard
              className={cardSpan}
              events={patient.events}
              alerts={patient.alerts}
            />
          )}
          {/* Devices + schedule need no monitoring routers; only the
              compliance score does, so that row is hidden without access. */}
          <DevicesComplianceCard
            className={cardSpan}
            config={data.self_measurement_config}
            schedules={patient.schedules?.data}
            compliance={patient.compliance}
            showCompliance={showMonitoring}
          />
        </div>

        {/* Collapse control under the cards, right-aligned. On the profile
            page this is hidden (showCollapseToggle=false) — the chart tabs'
            measurements row provides it instead. */}
        {showCollapseToggle && (
          <div className="mt-2 flex justify-end">
            <Button
              variant="subtle"
              size="sm"
              onClick={() => {
                patient.headerCollapsed = true;
              }}
            >
              <ChevronUp className="size-4" />
              <Trans>Collapse</Trans>
              <ShortcutHint>⌘I</ShortcutHint>
            </Button>
          </div>
        )}
      </div>
    );
  },
);

function SummaryPill({
  icon,
  children,
  className,
  ...rest
}: { icon: ReactNode; children: ReactNode } & HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md border border-border bg-muted px-2 py-1 text-xs text-muted-foreground${className ? ` ${className}` : ""}`}
      {...rest}
    >
      {icon}
      {children}
    </span>
  );
}

function ComplianceTooltip(props: {
  config?: MeasurementRequiredData_Output[] | null;
  schedules?: PatientScheduleResponse[];
  compliance?: ComplianceResponse;
}) {
  const percents = props.compliance
    ? compliancePercents(props.compliance)
    : null;
  // The day-exact ratio is only worth a line when the tolerance moved it.
  const hardPercent = percents?.differs ? percents.hard : null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <div className="font-semibold">
          <Trans>Devices</Trans>
        </div>
        <div className="font-normal">
          {props.config?.length ? (
            <DeviceConfigIcons config={props.config} align="left" />
          ) : (
            "—"
          )}
        </div>
      </div>
      <div className="flex flex-col gap-0.5">
        <div className="font-semibold">
          <Trans>Schedule</Trans>
        </div>
        <div className="font-normal">
          {props.schedules?.length
            ? props.schedules.map((s) => (
                <div key={s.uuid}>{scheduleToString(s)}</div>
              ))
            : "—"}
        </div>
      </div>
      <div className="flex flex-col gap-0.5">
        <div className="font-semibold">
          <Trans>Compliance</Trans>
        </div>
        <div className="font-normal">
          {percents != null ? `${percents.soft}%` : "—"}
        </div>
        {hardPercent != null && (
          <div className="font-normal opacity-75">
            <Trans>{hardPercent}% day-exact</Trans>
          </div>
        )}
      </div>
    </div>
  );
}

function PatientInfoCard(props: {
  className?: string;
  patientId: number;
  fullName: string;
  age: number | null;
  sexLetter: string;
  dateOfBirth?: string | null;
  regionName?: string;
  country?: string;
  clinicalRiskScore: number | string | null;
  gdmt: { result?: GDMTResult; loading: boolean };
  patientState?: PatientState;
  comorbidities?: ComorbiditySummary;
  showStateBadge: boolean;
  showWaitingRoom: boolean;
  patientStatus?: PatientStatus;
  callStatus?: PatientMonitoringCallStatus;
  reportStatus?: PatientMonitoringReportStatus;
  monitoringRoom?: PatientMonitoringWatchKind;
  watchStatus?: PatientMonitoringWatchStatus;
  race?: string;
  height?: number | string | null;
}) {
  const { t } = useLingui();
  const location = [props.regionName, props.country].filter(Boolean).join(", ");
  const physical = [
    props.race,
    props.height != null && props.height !== ""
      ? `${floatRounding(props.height, 1)}cm`
      : null,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div
      className={cn(
        "min-w-0 rounded-md border p-4 flex flex-col gap-3",
        props.className,
      )}
    >
      {/* Row 1: ID, name, age+sex */}
      <div className="text-base font-semibold flex items-center gap-2 flex-wrap">
        <span>
          <span className="text-muted-foreground">#{props.patientId}</span>{" "}
          {props.fullName}
        </span>
        <AgeSexBadge
          age={props.age}
          sexLetter={props.sexLetter}
          dateOfBirth={props.dateOfBirth}
        />
      </div>

      {/* Row 2: location | physical — wraps so a long region name drops to
          its own line rather than past the card. */}
      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5 text-sm">
        <span className="min-w-0">{physical || "—"}</span>
        <span className="min-w-0">{location || "—"}</span>
      </div>

      {/* Row 3a: comorbidity pills (only when comorbidities are recorded) */}
      <ComorbidityPills comorbidities={props.comorbidities} />

      {/* Row 3: clinical risk score | patient state | GDMT score */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <RiskScoreBadge score={props.clinicalRiskScore} />
        {props.showStateBadge && props.patientState != null && (
          <PatientStateBadge state={props.patientState} />
        )}
        {props.gdmt.result ? (
          <GdmtIndicator result={props.gdmt.result} />
        ) : (
          props.gdmt.loading && (
            // Ring-sized placeholder so the row doesn't shift once loaded.
            <div className="bg-muted-foreground/20 size-10 animate-pulse rounded-full motion-reduce:animate-none" />
          )
        )}
      </div>

      <div className="border-t" />

      <div className="flex flex-wrap gap-x-3 gap-y-2">
        <StatusCell
          label={t`Patient status`}
          value={
            props.patientStatus != null
              ? resolveEnumLabel(patientStatuses[props.patientStatus])
              : null
          }
        />
        {props.showWaitingRoom && (
          <>
            <StatusCell
              label={t`Call status`}
              value={
                props.callStatus != null
                  ? resolveEnumLabel(callStatuses[props.callStatus])
                  : null
              }
            />
            <StatusCell
              label={t`Report status`}
              value={
                props.reportStatus != null
                  ? resolveEnumLabel(reportStatuses[props.reportStatus])
                  : null
              }
            />
            <StatusCell
              label={t`Monitoring room`}
              value={
                props.monitoringRoom != null
                  ? resolveEnumLabel(watchKinds[props.monitoringRoom])
                  : null
              }
            />
            <StatusCell
              label={t`Watch status`}
              value={
                props.watchStatus != null
                  ? resolveEnumLabel(watchStatuses[props.watchStatus])
                  : null
              }
            />
          </>
        )}
      </div>
    </div>
  );
}

const COMORBIDITY_PILL_CLASS =
  "inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium";

function ComorbidityPills(props: { comorbidities?: ComorbiditySummary }) {
  const { t } = useLingui();
  const c = props.comorbidities;
  if (!c) {
    return null;
  }
  const pills = [
    { label: t`HF type`, value: c.hfType },
    { label: "AFib", value: c.afib },
    { label: "NYHA", value: c.nyhaClass },
  ].filter((p) => p.value != null);

  if (pills.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {pills.map((p) => (
        <span key={p.label} className={COMORBIDITY_PILL_CLASS}>
          <span className="text-muted-foreground">{p.label}</span>
          {p.value}
        </span>
      ))}
    </div>
  );
}

function StatusCell(props: { label: string; value: string | null }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-20">
      <div className="text-2xs text-muted-foreground">{props.label}</div>
      <div className="text-xs font-medium wrap-break-word">
        {props.value ?? "—"}
      </div>
    </div>
  );
}

type RiskLevel = "green" | "yellow" | "orange" | "red" | "none";

const riskClasses: Record<
  RiskLevel,
  { bg: string; border: string; text: string }
> = {
  green: {
    bg: "bg-success-soft",
    border: "border border-success-border",
    text: "text-success-foreground",
  },
  yellow: {
    bg: "bg-warning-soft",
    border: "border border-warning-border",
    text: "text-warning-foreground",
  },
  orange: {
    bg: "bg-alert-soft",
    border: "border border-alert-border",
    text: "text-alert-foreground",
  },
  red: {
    bg: "bg-danger-soft",
    border: "border border-danger-border",
    text: "text-danger-foreground",
  },
  none: {
    bg: "bg-muted",
    border: "border border-border",
    text: "text-muted-foreground",
  },
};

function riskLevel(score: number): RiskLevel {
  if (score <= 1) return "green";
  if (score <= 3) return "yellow";
  if (score <= 6) return "orange";
  return "red";
}

function RiskScoreBadge(props: { score: number | string | null }) {
  const hasScore = props.score != null && props.score !== "";
  const numeric = hasScore ? Number(props.score) : null;
  const level =
    numeric != null && !isNaN(numeric) ? riskLevel(numeric) : "none";
  const { bg, border, text } = riskClasses[level];
  return (
    <div className="flex items-center gap-2">
      <div
        className={`rounded-md px-3 py-1 min-w-12 text-center ${bg} ${border}`}
      >
        <div className={`text-xl font-bold leading-none ${text}`}>
          {hasScore ? props.score : "—"}
        </div>
      </div>
      <span className="text-xs text-muted-foreground leading-tight">
        <Trans>
          Clinical
          <br />
          risk score
        </Trans>
      </span>
    </div>
  );
}

function PatientStateBadge(props: { state: PatientState; compact?: boolean }) {
  const isHighRisk = props.state === PatientState.high_risk;
  const label = resolveEnumLabel(patientStates[props.state]);
  const classes = isHighRisk
    ? "bg-danger-soft border border-danger-border text-danger-foreground"
    : "bg-success-soft border border-success-border text-success-foreground";
  const pill = (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${classes}`}>
      {label}
    </span>
  );
  if (props.compact) return pill;
  return (
    <div className="flex items-center gap-2">
      {pill}
      <span className="text-xs text-muted-foreground leading-tight">
        <Trans>Patient state</Trans>
      </span>
    </div>
  );
}

const CARD_CLASS = "min-w-0 rounded-md border p-4 flex flex-col gap-2 min-h-24";
const CARD_TITLE_CLASS =
  "text-xs font-semibold text-muted-foreground uppercase tracking-wide";
const SUB_LABEL_CLASS = "text-2xs text-muted-foreground uppercase";

function NotesCard(props: {
  className?: string;
  description?: string | null;
  medicalNote?: string | null;
  technicalNote?: string | null;
  showMonitoringNotes: boolean;
}) {
  return (
    <div className={cn(CARD_CLASS, props.className)}>
      <div className={CARD_TITLE_CLASS}>
        <Trans>Description</Trans>
      </div>
      <div className="flex flex-col gap-2 max-h-56 overflow-y-auto">
        <NoteText content={props.description} />
        {props.showMonitoringNotes && (
          <>
            <div className="text-xs font-bold">
              <Trans>Medical note</Trans>
            </div>
            <NoteText content={props.medicalNote} html />
            <div className="text-xs font-bold">
              <Trans>Technical note</Trans>
            </div>
            <NoteText content={props.technicalNote} html />
          </>
        )}
      </div>
    </div>
  );
}

function NoteText(props: { content?: string | null; html?: boolean }) {
  if (props.html && props.content) {
    return (
      <RichTextContent
        className="prose prose-sm text-xs max-w-full"
        value={props.content}
      />
    );
  }
  return (
    <div className="text-xs whitespace-pre-wrap wrap-break-word">
      {props.content || <span className="text-muted-foreground italic">—</span>}
    </div>
  );
}

function MonitoringInfoCard(props: {
  className?: string;
  events?: DefaultPatientEventResponse[];
  alerts?: DefaultPatientAlertResponse[];
}) {
  const unseenAlerts = (props.alerts ?? [])
    .filter((a) => !a.alert_seen)
    .sort(
      (a, b) =>
        new Date(b.alert_timestamp).getTime() -
        new Date(a.alert_timestamp).getTime(),
    )
    .slice(0, 3);

  return (
    <div className={cn(CARD_CLASS, props.className)}>
      <div className={CARD_TITLE_CLASS}>
        <Trans>Monitoring Info</Trans>
      </div>

      {props.alerts != null && (
        <>
          <div className={`${SUB_LABEL_CLASS} mt-1`}>
            <Trans>Last alerts</Trans>
          </div>
          {unseenAlerts.length > 0 ? (
            <div className="flex flex-col gap-1">
              {unseenAlerts.map((alert) => (
                <AlertRow key={alert.alert_item_uuid} alert={alert} />
              ))}
            </div>
          ) : (
            <NoteText content={null} />
          )}
        </>
      )}

      {props.events != null && <MonitoringActivity events={props.events} />}
    </div>
  );
}

const CALL_CATEGORIES: EventCategory[] = [
  EventCategory.medical_call,
  EventCategory.technical_call,
];

/**
 * The "Monitoring Activity" section: the latest call (medical or technical) and
 * the latest treating-physician notification, with a dialog listing every
 * event when the user wants the full history.
 */
function MonitoringActivity(props: { events: DefaultPatientEventResponse[] }) {
  const { t } = useLingui();
  const sorted = props.events
    .slice()
    .sort(
      (a, b) =>
        new Date(b.event_date).getTime() - new Date(a.event_date).getTime(),
    );
  const lastCall = sorted.find((e) =>
    CALL_CATEGORIES.includes(e.event_category),
  );
  const lastReport = sorted.find(
    (e) => e.event_category === EventCategory.physician_notified,
  );

  return (
    <Dialog>
      <div className="mt-1 flex items-center justify-between gap-2">
        <div className={SUB_LABEL_CLASS}>
          <Trans>Monitoring Activity</Trans>
        </div>
        {sorted.length > 0 && (
          <Tooltip content={<Trans>All events</Trans>}>
            <DialogTrigger asChild>
              <button
                type="button"
                aria-label={t`All events`}
                className="inline-flex cursor-pointer items-center rounded-md border border-border bg-muted p-1 text-muted-foreground hover:shadow-sm"
              >
                <List className="size-3" />
              </button>
            </DialogTrigger>
          </Tooltip>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <ActivityRow label={t`Last call`} event={lastCall} />
        <ActivityRow label={t`Last report`} event={lastReport} />
      </div>

      <EventsDialogContent events={sorted} />
    </Dialog>
  );
}

function ActivityRow(props: {
  label: string;
  event?: DefaultPatientEventResponse;
}) {
  const { event } = props;
  return (
    // The label sits on its own line so the marker, category and date get the
    // full card width — on narrow screens a single row truncated the category
    // down to a few characters.
    <div className="flex flex-col gap-0.5 text-xs">
      <span className="text-2xs text-muted-foreground">{props.label}</span>
      {event ? (
        <div className="flex items-center gap-2">
          <EventMarker
            shape={event.event_shape}
            colorHex={event.event_color_hex}
          />
          <span className="min-w-0 truncate font-bold">
            {resolveEnumLabel(eventCategory[event.event_category])}
          </span>
          <span className="ml-auto shrink-0 text-muted-foreground">
            {dateTimeOrNull(event.event_date, false)}
          </span>
        </div>
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </div>
  );
}

function EventsDialogContent(props: { events: DefaultPatientEventResponse[] }) {
  return (
    <DialogContent className="w-120">
      <DialogHeader>
        <DialogTitle>
          <Trans>Monitoring activity</Trans>
        </DialogTitle>
        <div className="text-sm text-muted-foreground">
          <Plural value={props.events.length} one="# event" other="# events" />
        </div>
      </DialogHeader>
      {props.events.length > 0 ? (
        <div className="flex max-h-96 flex-col divide-y divide-border overflow-y-auto">
          {props.events.map((event, i) => (
            <div key={event.event_item_uuid ?? i} className="py-2 first:pt-0">
              <EventRow event={event} />
            </div>
          ))}
        </div>
      ) : (
        <NoteText content={null} />
      )}
    </DialogContent>
  );
}

function EventRow(props: { event: DefaultPatientEventResponse }) {
  const { event } = props;
  const typeLabel = event.event_category_type
    ? resolveEnumLabel(eventCategoryType[event.event_category_type])
    : null;
  const detailsLabel = event.event_category_details
    ? resolveEnumLabel(eventCategoryDetails[event.event_category_details])
    : null;
  const description = event.event_description?.trim();
  const comment = event.event_comment?.trim();

  return (
    <div className="flex flex-col gap-0.5 text-xs">
      <div className="flex items-center gap-2">
        <EventMarker
          shape={event.event_shape}
          colorHex={event.event_color_hex}
        />
        <span className="min-w-0 truncate">
          <span className="font-bold">
            {resolveEnumLabel(eventCategory[event.event_category])}
          </span>
          {typeLabel ? ` · ${typeLabel}` : ""}
          {detailsLabel ? ` · ${detailsLabel}` : ""}
        </span>
        <span className="ml-auto shrink-0 text-muted-foreground">
          {dateTimeOrNull(event.event_date, false)}
        </span>
      </div>
      {/* Indented under the marker: rich-text description, then any comment. */}
      {description && (
        <RichTextContent
          className="prose prose-sm max-w-full pl-6 text-xs"
          value={description}
        />
      )}
      {comment && (
        <RichTextContent
          className="prose prose-sm max-w-full pl-6 text-2xs text-muted-foreground"
          value={comment}
        />
      )}
    </div>
  );
}

function AlertRow(props: { alert: DefaultPatientAlertResponse }) {
  const { alert } = props;
  return (
    <div className="flex items-center gap-2 text-xs">
      <AlertSeverityPill alertType={alert.alert_type} />
      <span className="min-w-0 truncate font-bold">
        {resolveEnumLabel(alertKinds[alert.alert_kind])}
      </span>
      <span className="ml-auto shrink-0 text-muted-foreground">
        {dateTimeOrNull(alert.alert_timestamp, false)}
      </span>
    </div>
  );
}

function AlertSeverityPill(props: { alertType: AlertType }) {
  const severity = Math.abs(props.alertType);
  const classes =
    severity >= 3
      ? "bg-danger-soft border border-danger-border text-danger-foreground"
      : severity === 2
        ? "bg-alert-soft border border-alert-border text-alert-foreground"
        : severity === 1
          ? "bg-warning-soft border border-warning-border text-warning-foreground"
          : "bg-muted border border-border text-muted-foreground";
  return (
    <span
      className={`inline-flex items-center justify-center shrink-0 w-6 h-4 rounded-full text-2xs font-semibold ${classes}`}
    >
      {props.alertType}
    </span>
  );
}

function DevicesComplianceCard(props: {
  className?: string;
  config?: MeasurementRequiredData_Output[] | null;
  schedules?: PatientScheduleResponse[];
  compliance?: ComplianceResponse;
  /** The compliance endpoint sits behind the monitoring routers — hide the
   *  row (rather than a misleading permanent "—") for users without access. */
  showCompliance?: boolean;
}) {
  const { t } = useLingui();
  return (
    <div className={cn(CARD_CLASS, "gap-3", props.className)}>
      <div className={CARD_TITLE_CLASS}>
        <Trans>Devices &amp; Compliance</Trans>
      </div>

      <LabeledRow label={t`Devices`}>
        {props.config?.length ? (
          <DeviceConfigIcons config={props.config} align="left" />
        ) : (
          <NoteText content={null} />
        )}
      </LabeledRow>

      <LabeledRow label={t`Schedule`}>
        {props.schedules?.length ? (
          <div className="flex flex-col gap-0.5 wrap-break-word">
            {props.schedules.map((s) => (
              <span key={s.uuid}>{scheduleToString(s)}</span>
            ))}
          </div>
        ) : (
          <NoteText content={null} />
        )}
      </LabeledRow>

      {(props.showCompliance ?? true) && (
        <LabeledRow label={t`Compliance`} info={<ComplianceInfoTooltip />}>
          <ComplianceScore compliance={props.compliance} />
        </LabeledRow>
      )}
    </div>
  );
}

function LabeledRow(props: {
  label: string;
  info?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className={`${SUB_LABEL_CLASS} flex items-center gap-1`}>
        {props.label}
        {props.info}
      </div>
      <div className="text-xs">{props.children}</div>
    </div>
  );
}
